import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSetTopbarCrumbs } from "@marekl/adminer/shell";
import {
  Page,
  Input,
  Select,
  FilterField,
  FilterPanel,
  Button,
  EmptyState,
  InlineLoading,
  Table,
  Pagination,
  Badge,
  DatePicker,
  type Column,
} from "@marekl/adminer/components";
import {
  useSearchVouchers,
  type VoucherResponse,
  type VoucherSource,
  type VoucherState,
} from "../../lib/queries/vouchers";
import { useVoucherTypes } from "../../lib/queries/voucherTypes";
import {
  useDebouncedSearchFilter,
  useOffsetPaginatedQuery,
} from "@marekl/adminer/hooks";
import { parseApiDate, toApiDate } from "@marekl/adminer/datetime";
import { formatDate } from "@marekl/adminer/format";
import { extractVoucherId } from "../../lib/barcode";
import { useAuth } from "../../auth/useAuth";
import styles from "./VouchersPage.module.css";

const STATE_LABEL: Record<VoucherState, string> = {
  active: "Aktivní",
  depleted: "Vyčerpán",
  expired: "Expirován",
  cancelled: "Stornován",
};

const STATE_VARIANT: Record<VoucherState, "ok" | "warn" | "err" | "muted"> = {
  active: "ok",
  depleted: "muted",
  expired: "err",
  cancelled: "warn",
};

const SOURCE_LABEL: Record<VoucherSource, string> = {
  eshop: "E-shop",
  counter: "Recepce",
};

const PER_PAGE_OPTIONS = [25, 50, 100];

export function VouchersPage() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const isManager =
    user?.role === "manager" || user?.role === "superadmin";

  const q = params.get("q") ?? "";
  const state = (params.get("state") ?? "") as VoucherState | "";
  const source = (params.get("source") ?? "") as VoucherSource | "";
  const voucherTypeID = params.get("voucher_type_id") ?? "";
  const issuedFrom = params.get("issued_from") ?? "";
  const issuedUntil = params.get("issued_until") ?? "";

  const hasFilter = !!(q || state || voucherTypeID || source || issuedFrom || issuedUntil);

  const types = useVoucherTypes();
  const filters = useMemo(
    () => ({
      q,
      state,
      source,
      voucher_type_id: voucherTypeID,
      issued_from: issuedFrom,
      issued_until: issuedUntil,
      // Reception users must apply at least one filter before results are shown.
      // The backend enforces this too; the frontend gate prevents even a request.
      enabled: isManager || hasFilter,
    }),
    [q, state, source, voucherTypeID, issuedFrom, issuedUntil, isManager, hasFilter],
  );

  const result = useOffsetPaginatedQuery(useSearchVouchers, filters, {
    searchParams: params,
    setSearchParams: setParams,
    defaultLimit: 25,
    limitOptions: PER_PAGE_OPTIONS,
    summaryNounForms: ["voucher", "vouchery", "voucherů"],
  });

  const search = useDebouncedSearchFilter({
    value: q,
    onCommit: (val) => result.setFilters({ q: val }),
  });

  const columns: Column<VoucherResponse>[] = [
    {
      key: "barcode",
      header: "Čárový kód",
      render: (v) => (
        <Link to={`/vouchers/${v.id}`} className={styles.mono}>
          {v.barcode}
        </Link>
      ),
    },
    {
      key: "ticket_id",
      header: "Voucher ID",
      // Redundant with the barcode on a phone card — drop it there.
      cardHidden: true,
      render: (v) => {
        const tid = extractVoucherId(v.barcode);
        return tid ? <span className={styles.mono}>{tid}</span> : null;
      },
      tdClass: styles.colDate,
    },
    {
      key: "customer",
      header: "Zákazník",
      primary: true,
      render: (v) => (
        <div>
          <div>{v.customer_name}</div>
          <div className={styles.contact}>
            {v.customer_phone ?? v.customer_email ?? "—"}
          </div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Typ",
      render: (v) => v.voucher_type?.name ?? "—",
    },
    ...(isManager
      ? ([
          {
            key: "source",
            header: "Zdroj",
            render: (v: VoucherResponse) => SOURCE_LABEL[v.source],
          },
        ] as Column<VoucherResponse>[])
      : []),
    {
      key: "rides",
      header: "Zbývá",
      render: (v) =>
        v.state === "cancelled" ? (
          "—"
        ) : (
          <span>
            <strong>{v.rides_remaining}</strong> / {v.rides_total}
          </span>
        ),
    },
    {
      key: "issued",
      header: "Vydáno",
      render: (v) => formatDate(v.created_at),
      tdClass: styles.colDate,
    },
    {
      key: "valid_until",
      header: "Platnost do",
      render: (v) => formatDate(v.valid_until),
      tdClass: styles.colDate,
    },
    {
      key: "state",
      header: "Stav",
      render: (v) => (
        <Badge variant={STATE_VARIANT[v.state]}>{STATE_LABEL[v.state]}</Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (v) => (
        <Table.Actions>
          <Table.Action to={`/vouchers/${v.id}`}>Otevřít</Table.Action>
        </Table.Actions>
      ),
    },
  ];

  useSetTopbarCrumbs([{ label: "Vouchery" }]);

  const showResults = isManager || hasFilter;

  return (
    <>
      <Page.Content>
        <Page.Header
          title={isManager ? "Správa voucherů" : "Vyhledat voucher"}
          subtitle={
            isManager
              ? "přehled všech vydaných voucherů"
              : "kód, jméno, telefon nebo e-mail zákazníka"
          }
          subtitleVariant="tagline"
        />

        <FilterPanel
          formProps={{
            onSubmit: (e) => {
              e.preventDefault();
              search.flush();
            },
          }}
        >
          <FilterField wide>
            <Input
              label="Hledat"
              type="search"
              placeholder='např. „Novák", „603…", „8594000…", ID'
              value={search.draft}
              onChange={(e) => search.setDraft(e.target.value)}
              autoFocus
            />
          </FilterField>
          <FilterField>
            <Select
              label="Typ voucheru"
              id="vtype"
              value={voucherTypeID}
              onChange={(e) =>
                result.setFilters({ voucher_type_id: e.target.value || undefined })
              }
            >
              <option value="">Všechny</option>
              {types.data?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </FilterField>
          <FilterField>
            <Select
              label="Stav"
              id="vstate"
              value={state}
              onChange={(e) =>
                result.setFilters({ state: e.target.value || undefined })
              }
            >
              <option value="">Všechny</option>
              <option value="active">Aktivní</option>
              <option value="depleted">Vyčerpané</option>
              <option value="cancelled">Stornované</option>
              <option value="expired">Expirované</option>
            </Select>
          </FilterField>
          {isManager && (
            <>
              <FilterField>
                <Select
                  label="Zdroj"
                  id="vsource"
                  value={source}
                  onChange={(e) =>
                    result.setFilters({ source: e.target.value || undefined })
                  }
                >
                  <option value="">Všechny</option>
                  <option value="eshop">E-shop</option>
                  <option value="counter">Recepce</option>
                </Select>
              </FilterField>
              <FilterField>
                <DatePicker
                  label="Vydáno od"
                  value={parseApiDate(issuedFrom)}
                  onChange={(d) =>
                    result.setFilters({ issued_from: toApiDate(d) ?? undefined })
                  }
                />
              </FilterField>
              <FilterField>
                <DatePicker
                  label="Vydáno do"
                  value={parseApiDate(issuedUntil)}
                  onChange={(d) =>
                    result.setFilters({ issued_until: toApiDate(d) ?? undefined })
                  }
                />
              </FilterField>
            </>
          )}
          <FilterField action>
            <Button type="submit" variant="primary" block>
              Hledat
            </Button>
          </FilterField>
        </FilterPanel>

        {showResults ? (
          <>
            <Page.SectionHeader
              title="Výsledky"
              meta={
                [
                  result.isFetching && !result.isLoading ? "Aktualizuji…" : null,
                  result.pagination.summary,
                ]
                  .filter(Boolean)
                  .join(" · ") || undefined
              }
            />

            {result.isLoading ? (
              <InlineLoading mono>Načítáme…</InlineLoading>
            ) : (
              <Table
                cards
                columns={columns}
                rows={result.data?.items ?? []}
                rowKey={(v) => v.id}
                error={
                  result.isError
                    ? "Vyhledávání se nezdařilo. Zkuste to prosím znovu."
                    : undefined
                }
                onRetry={result.refetch}
                empty={
                  <EmptyState title="Žádný voucher nevyhovuje zadaným filtrům." />
                }
              />
            )}

            <Pagination {...result.pagination} />
          </>
        ) : (
          <EmptyState title="Začněte zadáním hledaného výrazu nebo upřesněte filtry." />
        )}
      </Page.Content>
    </>
  );
}
